import "./SupabaseComponent.scss";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button.tsx";
import { ImageConfig } from "@/constants";
import { BackIcon } from "@/constants/ImageConfig.ts";
import LoaderComponent from "@/components/common-components/loader/LoaderComponent.tsx";
import StatusCardComponent from "@/components/common-components/status-card/StatusCardComponent.tsx";
import commonService from "@/services/common.service.ts";
import { LOCAL_STORAGE_LOGGED_IN_USER_DATA } from "@/constants/Misc.tsx";
import CreateSupabaseProjectComponent
    from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/create-supabase-project/CreateSupabaseProjectComponent.tsx";
import { updateProjectDetails } from "@/store/actions/projectdata.action.ts";
import { useDispatch } from "react-redux";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import ConditionalTooltipComponent from "@/components/common-components/conditional-tooltip/ConditionalTooltipComponent.tsx";

interface SupabaseComponentProps {
    isOpen: boolean;
    onClose: () => void;
    projectId: string;
    currentProjectDetails: any;
}

const SupabaseComponent = (props: SupabaseComponentProps) => {
    // State management
    const { isOpen, onClose, projectId, currentProjectDetails } = props;
    const isMobile = useIsMobile();

    const [currentStep, setCurrentStep] = useState("projects");
    const [projectsList, setProjectsList] = useState<any[]>([]);
    const [isProjectListLoading, setIsProjectListLoading] = useState(false);
    const [isSupabaseProjectConnecting, setIsSupabaseProjectConnecting] = useState(false);
    const [isPausingOrRestoring, setIsPausingOrRestoring] = useState<string | null>(null);
    const currentSupabaseProjectRef = useRef<string>('');
    const hasInitializedRef = useRef<boolean>(false);
    const currentUser = JSON.parse(localStorage.getItem(LOCAL_STORAGE_LOGGED_IN_USER_DATA) || '{}');
    const dispatch = useDispatch();

    const handleSupabaseLogin = useCallback(() => {
        setIsProjectListLoading(true);
        commonService._project_service.supabaseLogin({})
            .then((response: any) => {
                if (response?.data?.auth_url) {
                    window.open(response?.data?.auth_url, '_blank');
                }
            })
            .catch((error: any) => {
                console.error(error);
                commonService._alert.showToast("Error logging in to Supabase.", "error");
            })
            .finally(() => {
                setIsProjectListLoading(false);
            });
    }, []);

    // Fetch Supabase projects
    const fetchSupabaseProjects = useCallback(() => {
        setIsProjectListLoading(true);
        commonService._project_service.getSupabaseProjects()
            .then((response: any) => {
                setProjectsList(response.data || []);
            })
            .catch((error: any) => {
                console.error(error);
                commonService._alert.showToast("Error fetching Supabase projects.", "error");
            })
            .finally(() => {
                setIsProjectListLoading(false);
            });
    }, []);

    const handleSupabaseProjectConnect = useCallback((supabase_project_id: string) => {
        dispatch(updateProjectDetails({
            supabase_creds: {
                supabase_project_id: supabase_project_id,
            }
        }));
    }, [dispatch]);

    // Handle project selection
    const handleSupabaseProjectSelect = useCallback((projectId: string, supaProjectId: string) => {
        currentSupabaseProjectRef.current = supaProjectId;
        setIsSupabaseProjectConnecting(true);


        const payload = { supabase_project_id: supaProjectId };

        commonService._project_service.connectSupbaseToProject(projectId, payload)
            .then(() => {
                handleSupabaseProjectConnect( supaProjectId);
                setIsSupabaseProjectConnecting(false);
                commonService._alert.showToast("Supabase project connected successfully.", "success");
                onClose()
                // Emit success event to clear Supabase errors
                commonService._communications.supabaseConnectedSuccessSubject.next();
            })
            .catch((err: any) => {
                setIsSupabaseProjectConnecting(false);

                // Try extracting API error message
                let errorMessage = "Error connecting Supabase project.";
                if (err?.detail?.error) {
                    errorMessage = err.detail.error;
                }

                commonService._alert.showToast(errorMessage, "error");
            });
    }, [handleSupabaseProjectConnect,onClose]);


    const handleProjectDisconenct = useCallback((projectId: string, supaProjectId: string) => {
        currentSupabaseProjectRef.current = supaProjectId;
        setIsSupabaseProjectConnecting(true)
        const payload = {
            supabase_project_id: supaProjectId
        };
        commonService._project_service.disconnectSupbaseFromProject(projectId, payload)
            .then(() => {
                currentSupabaseProjectRef.current = '';
                handleSupabaseProjectConnect( "");
                setIsSupabaseProjectConnecting(false);
            })
            .catch(() => {
                setIsSupabaseProjectConnecting(false);
                commonService._alert.showToast("Error disconnecting Supabase project.", "error");
            });
    }, [handleSupabaseProjectConnect])

    const handlePauseProject = useCallback((supaProjectId: string) => {
        setIsPausingOrRestoring(supaProjectId);
        commonService._project_service.pauseSupabaseProject(projectId, { supabase_project_id: supaProjectId })
            .then(() => {
                commonService._alert.showToast("Project paused successfully.", "success");
                //TODO update the project list with the new status instead of api call
                fetchSupabaseProjects(); // Refresh the list
            })
            .catch((error:any) => {
                commonService._alert.showToast(error?.detail?.error || "Error pausing project.", "error");
            })
            .finally(() => {
                setIsPausingOrRestoring(null);
            });
    }, [fetchSupabaseProjects, projectId]);

    const handleRestoreProject = useCallback((supaProjectId: string) => {
        setIsPausingOrRestoring(supaProjectId);
        commonService._project_service.restoreSupabaseProject(projectId, { supabase_project_id: supaProjectId })
            .then(() => {
                commonService._alert.showToast("Project restored successfully.", "success");
                //TODO update the project list with the new status instead of api call
                fetchSupabaseProjects(); // Refresh the list
            })
            .catch((error:any) => {
                commonService._alert.showToast(error?.detail?.error || "Error restoring project.", "info");
            })
            .finally(() => {
                setIsPausingOrRestoring(null);
            });
    }, [fetchSupabaseProjects, projectId]);

    // Handle storage changes for user data
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === LOCAL_STORAGE_LOGGED_IN_USER_DATA) {
                fetchSupabaseProjects();
            }
        };
        window.addEventListener("storage", handleStorageChange);
        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [fetchSupabaseProjects]);

    // Load projects when dialog opens - Fixed to prevent infinite loops
    useEffect(() => {
        if (isOpen && !hasInitializedRef.current) {
            hasInitializedRef.current = true;
            if (!currentUser?.supabase_cred_id) {
                handleSupabaseLogin();
            } else {
                setProjectsList([]);
                fetchSupabaseProjects();
            }
        }
        // Reset initialization flag when dialog closes
        if (!isOpen) {
            hasInitializedRef.current = false;
        }
    }, [isOpen, fetchSupabaseProjects, handleSupabaseLogin, currentUser?.supabase_cred_id]);

    const handleConnectDisconnectProject = useCallback((projectId: string, supaProjectId: string) => {
        if (currentProjectDetails?.supabase_creds?.supabase_project_id !== supaProjectId) {
            handleSupabaseProjectSelect(projectId, supaProjectId);
        } else {
            handleProjectDisconenct(projectId, supaProjectId);
        }
    }, [handleSupabaseProjectSelect, handleProjectDisconenct, currentProjectDetails])


    // Shared content for both mobile and desktop
    const renderContent = () => (
        <>
                {currentStep !== "projects" && (
                <div className="flex justify-start">
                    <Button
                        size="sm"
                        className="text-secondary widget-10 mt-3 ml-6"
                        variant="text"
                        prefixIcon={<BackIcon />}
                        onClick={() => setCurrentStep("projects")}
                        mixpanelEvent={{
                            name: "supabase_back_to_projects_clicked",
                            properties: { project_id: projectId }
                        }}
                    >
                        Back
                    </Button>
                </div>
            ) as ReactNode}
            <div className={`flex flex-col justify-center ${isMobile ? 'p-6' : 'p-10'}`}>
                <div className="flex justify-center">
                    <ImageConfig.SupabaseLogoIcon className="h-12 w-12 mb-4" />
                </div>

                {currentStep === "projects" ? (
                    <>
                        <div className={`text-center font-medium mb-2 ${isMobile ? 'text-xl' : 'text-2xl'}`}>
                            Select your database
                        </div>
                        <div className={`text-center mb-4 font-medium text-[var(--secondary-foreground)] ${isMobile ? 'text-sm px-4' : 'text-md px-20'}`}>
                            Connect with a Supabase project to manage your data, set up authentication, create backend
                            functions, and more.
                        </div>
                        {currentUser?.supabase_cred_id && (
                            <div className={`text-center text-sm mb-4 text-green-600 font-medium ${isMobile ? 'px-4' : 'px-20'}`}>
                                Connected to Supabase
                            </div>
                        )}
                        <div className={`supabase-project-cards-wrapper ${isMobile ? 'px-0' : 'px-20'}`}>
                            {isProjectListLoading && <LoaderComponent />}

                            {!isProjectListLoading && projectsList?.length > 0 ? (
                                projectsList.map((project) => (
                                    <div
                                        className={`project-card ${currentProjectDetails?.supabase_creds?.supabase_project_id === project?.id &&
                                            "!border-[var(--custom-button-highlight-border)] text-primary border-[0.25px] bg-[var(--custom-button-background-highlight)]"
                                            }`}
                                        key={project?.id}
                                    >
                                        <div className="project-details-wrapper">
                                            <ConditionalTooltipComponent
                                                text={project?.name || ""}
                                                className={`ellipsis-text ${currentProjectDetails?.supabase_creds?.supabase_project_id === project?.id &&
                                                    "text-primary"
                                                    }`}
                                            >
                                                <span>{project?.name}</span>
                                            </ConditionalTooltipComponent>
                                            <div
                                                className={`text-sm pt-1 ${project?.status === "INACTIVE"
                                                        ? "text-red-500"
                                                        : project?.status === "ACTIVE_HEALTHY"
                                                            ? "text-[var(--secondary-foreground)]"
                                                            : "text-yellow-500"
                                                    }`}
                                            >
                                                {project?.region} - {project?.status}
                                            </div>
                                        </div>
                                        <div className={`supabase-project-actions-wrapper gap-2  flex`}>
                                            {project?.status === "ACTIVE_HEALTHY" && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleConnectDisconnectProject(projectId, project?.id)}
                                                    loading={isSupabaseProjectConnecting && currentSupabaseProjectRef.current === project?.id}
                                                    loadingText="connecting"
                                                    className={isMobile ? 'w-full' : ''}
                                                    mixpanelEvent={{
                                                        name: currentProjectDetails?.supabase_creds?.supabase_project_id !== project?.id 
                                                            ? "supabase_project_connect_clicked" 
                                                            : "supabase_project_disconnect_clicked",
                                                        properties: { 
                                                            project_id: projectId, 
                                                            supabase_project_id: project?.id,
                                                            supabase_project_name: project?.name
                                                        }
                                                    }}
                                                >
                                                    {currentProjectDetails?.supabase_creds?.supabase_project_id !== project?.id ? "Connect" : "Disconnect"}
                                                </Button>
                                            )}

                                            {project?.status === "ACTIVE_HEALTHY" ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handlePauseProject(project?.id)}
                                                    loading={isPausingOrRestoring === project?.id}
                                                    className={isMobile ? 'w-full' : ''}
                                                    mixpanelEvent={{
                                                        name: "supabase_project_pause_clicked",
                                                        properties: { 
                                                            project_id: projectId, 
                                                            supabase_project_id: project?.id,
                                                            supabase_project_name: project?.name
                                                        }
                                                    }}
                                                >
                                                    Pause
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleRestoreProject(project?.id)}
                                                    loading={isPausingOrRestoring === project?.id}
                                                    className={isMobile ? 'w-full' : ''}
                                                    mixpanelEvent={{
                                                        name: "supabase_project_restore_clicked",
                                                        properties: { 
                                                            project_id: projectId, 
                                                            supabase_project_id: project?.id,
                                                            supabase_project_name: project?.name,
                                                            project_status: project?.status
                                                        }
                                                    }}
                                                >
                                                    Restore
                                                </Button>
                                            )}

                                        </div>
                                    </div>
                                ))
                            ) : (
                                <StatusCardComponent>
                                    {currentUser?.supabase_cred_id ? (
                                        "No Supabase projects found."
                                    ) : (
                                        <>
                                            <div className="text-center">
                                                Connect to Supabase to create and manage your projects.
                                            </div>
                                            <div className="d-flex justify-center">
                                                <Button
                                                    variant="image"
                                                    size="image"
                                                    className="p-0 pt-4"
                                                    onClick={handleSupabaseLogin}
                                                    mixpanelEvent={{
                                                        name: "supabase_login_clicked",
                                                        properties: { project_id: projectId }
                                                    }}
                                                >
                                                    <ImageConfig.SupabaseConnectIconSVG className="size-full h-full" />
                                                </Button>
                                            </div>
                                        </>
                                    ) as ReactNode}
                                </StatusCardComponent>
                            )}
                        </div>
                        <div className="flex justify-center mt-4">
                            {currentUser?.supabase_cred_id && (
                                <Button 
                                    onClick={() => setCurrentStep("create")}
                                    mixpanelEvent={{
                                        name: "supabase_create_new_project_clicked",
                                        properties: { project_id: projectId }
                                    }}
                                >
                                    Create New Project
                                </Button>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div className={`text-center font-medium mb-2 ${isMobile ? 'text-xl' : 'text-2xl'}`}>
                            Create new Supabase project
                        </div>
                        <CreateSupabaseProjectComponent
                            onCreate={(project: any) => {
                                setCurrentStep("projects");
                                setProjectsList((prevList) => {
                                    if (!prevList || prevList.length === 0) {
                                        return [project];
                                    }
                                    return [project, ...prevList];
                                });
                            }}
                        />
                    </>
                )}
            </div>
        </>
    );

    // Mobile view - bottom sheet
    if (isMobile) {
        return (
            <Sheet open={isOpen} onOpenChange={onClose}>
                <SheetContent side="bottom" className="h-[90vh] p-0 overflow-y-auto">
                    {renderContent()}
                </SheetContent>
            </Sheet>
        );
    }

    // Desktop view - dialog
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                {renderContent()}
            </DialogContent>
        </Dialog>
    );

};

export default SupabaseComponent;